<template>
  <div class="space-y-8">
    <template v-for="order in orders" :key="order.id">
      <div class="card">
        <div :class="{ 'bg-danger-500': order.blemish }" class="card-header flex justify-between">
          <div>
            <p class="pointer-events-none font-medium text-gray-900"> {{ order.product_sku }} - {{ order.product_name }} </p>

            <p class="pointer-events-none mt-1 flex items-center text-sm font-medium text-gray-900">
              Ordered on: <span class="text-steel-600 ml-2">{{ order.ordered_at_formatted }}</span>
            </p>

            <p v-if="order.status === 'printing'" class="pointer-events-none mt-1 flex items-center text-sm font-medium text-gray-900">
              Sent to print on: <span class="text-steel-600 ml-2">{{ order.sent_at_formatted }}</span>
            </p>

            <p v-if="order.status === 'received'" class="pointer-events-none mt-1 flex items-center text-sm font-medium text-gray-900">
              Sent to print on: <span class="text-steel-600 ml-2 font-semibold">{{ order.sent_at_formatted }}</span>
            </p>

            <p v-if="order.status === 'received'" class="pointer-events-none mt-1 flex items-center text-sm font-medium text-gray-900">
              Received on: <span class="text-steel-600 ml-2 font-semibold">{{ order.received_at_formatted }}</span>
            </p>
          </div>
          <div class="flex items-center space-x-2">
            <Link v-if="order.product_type === 'tshirt' && order.status === 'pending'" :href="$route('customs.receive', [order.id, 'printing'])" class="btn btn-success">Print </Link>

            <div v-if="order.status === 'printing'" class="flex items-center space-x-2">
              <form class="flex items-center space-x-2" @submit.prevent="blemish_form.post($route('customs.blemish', order.id))">
                <div class="flex rounded-md shadow-sm">
                  <input v-model="blemish_form.qty" class="w-20 rounded-none rounded-l-md border-gray-300 text-center focus:outline-none focus:ring-0 sm:text-sm" type="text" placeholder="QTY" required />
                  <button type="submit" class="btn-danger relative -ml-px inline-flex items-center space-x-2 rounded-r-md px-4 py-2 text-sm font-medium"> Blemish </button>
                </div>
              </form>
              <Link :href="$route('customs.receive', [order.id, 'received'])" class="btn btn-success">Receive</Link>
            </div>

            <form v-if="order.status === 'received'" class="flex items-center space-x-2" @submit.prevent="blemish_form.post($route('customs.blemish', order.id))">
              <div class="flex rounded-md shadow-sm">
                <input v-model="blemish_form.qty" class="w-20 rounded-none rounded-l-md border-gray-300 text-center focus:outline-none focus:ring-0 sm:text-sm" type="text" placeholder="QTY" required />
                <button type="submit" class="btn-danger relative -ml-px inline-flex items-center space-x-2 rounded-r-md px-4 py-2 text-sm font-medium"> Blemish </button>
              </div>
            </form>
          </div>
        </div>
        <div class="card-body">
          <div class="relative pt-1">
            <span :class="{ 'bg-primary-500': order.status !== 'rejected', 'bg-red-500': order.status === 'rejected' }" class="mb-4 inline-block rounded-full px-2 py-1 font-medium uppercase text-white">
              {{ order.status }}
            </span>
            <span v-if="order.blemish > 0" class="mb-4 ml-4 inline-block rounded-full border-2 border-red-500 px-2 py-1 font-medium uppercase text-red-500"> BLEMISH - {{ order.blemish }} </span>
            <div class="mb-4 flex h-2 overflow-hidden rounded bg-primary-100 text-xs">
              <div v-if="order.status === 'rejected'" class="flex w-full flex-col justify-center whitespace-nowrap bg-red-500 text-center text-white shadow-none"></div>

              <div v-if="order.status === 'approved'" class="flex w-1/3 flex-col justify-center whitespace-nowrap bg-green-400 text-center text-white shadow-none"></div>

              <template v-if="order.status === 'printing'">
                <div class="flex w-1/3 flex-col justify-center whitespace-nowrap bg-green-400 text-center text-white shadow-none"></div>
                <div class="flex w-1/3 flex-col justify-center whitespace-nowrap bg-green-500 text-center text-white shadow-none"></div>
              </template>

              <template v-if="order.status === 'received'">
                <div class="flex w-1/3 flex-col justify-center whitespace-nowrap bg-green-400 text-center text-white shadow-none"></div>
                <div class="flex w-1/3 flex-col justify-center whitespace-nowrap bg-green-500 text-center text-white shadow-none"></div>
                <div class="flex w-1/3 flex-col justify-center whitespace-nowrap bg-green-700 text-center text-white shadow-none"></div>
              </template>
            </div>
          </div>
          <ul role="list" class="z-10 grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 xl:gap-x-8">
            <template v-if="order.product_type !== 'embroidery'">
              <li v-for="data in order.data" :key="data.title" class="aspect-h-10 block w-full rounded-lg p-4 ring-2 ring-primary-200">
                <div class="space-y-6">
                  <p class="text-center font-medium text-gray-800">
                    {{ data.title }}
                  </p>
                  <img :src="data.filename" :alt="data.title" class="hover:opacity-75" />
                </div>
              </li>
            </template>

            <template v-else>
              <li v-for="data in order.data" :key="data.title" class="aspect-h-10 block w-full rounded-lg p-4 ring-2 ring-primary-200">
                <div class="space-y-6">
                  <p class="text-center font-medium text-gray-800">{{ data.title }}</p>
                  <img :src="data.filename" :alt="data.title" class="hover:opacity-75" :style="`background: ${data.background}`" />
                </div>
              </li>
              <li class="block w-full rounded-lg p-4 ring-2 ring-primary-200">
                <dl class="grid grid-cols-2">
                  <dt class="font-semibold">Text:</dt>
                  <dd>{{ order.data[0].item.text }}</dd>
                  <dt class="font-semibold">Font Family:</dt>
                  <dd>{{ order.data[0].item.font }}</dd>
                  <dt class="font-semibold">Font Size:</dt>
                  <dd>{{ order.data[0].item.size }}</dd>
                  <dt class="font-semibold">Font Color:</dt>
                  <dd>{{ order.data[0].item.color }}</dd>
                  <dt class="font-semibold">Text Effect:</dt>
                  <dd>{{ order.data[0].item.effect ?? "none" }}</dd>
                  <dt class="font-semibold">Width:</dt>
                  <dd>{{ order.data[0].item.width }}</dd>
                  <dt class="font-semibold">Height:</dt>
                  <dd>{{ order.data[0].item.height }}</dd>
                </dl>
              </li>
            </template>
          </ul>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { Link, useForm } from "@inertiajs/vue3";

const { orders } = defineProps(["orders"]);

const blemish_form = useForm({
  qty: null,
});
</script>
